import { MixMatchComponent } from "./mix-and-match-component";

export class MMBoxProduct extends MixMatchComponent {
  addButtonEl: HTMLElement | null;
  quantityPickerEl: HTMLElement | null;
  quantityInputEl: HTMLInputElement | null;
  plusButtonEl: HTMLElement | null;
  minusButtonEl: HTMLElement | null;
  
  variantId: string | number;
  productHandle: string | undefined;
  productTitle: string | undefined;
  productPrice: number;
  maxQuantity: number;
  productImage: string;
  quantity: number;

  constructor() {
    super();
    this._cacheUIElements();
    this._initializeState();
    this._bindEvents();
  }

  _cacheUIElements(): void {
    this.addButtonEl = this.querySelector(".js-box-add-button") as HTMLElement | null;
    this.quantityPickerEl = this.querySelector(".js-box-quantity-picker") as HTMLElement | null;
    this.quantityInputEl = this.querySelector(".js-qnt-input") as HTMLInputElement | null;
    this.plusButtonEl = this.querySelector(".js-plus") as HTMLElement | null;
    this.minusButtonEl = this.querySelector(".js-minus") as HTMLElement | null;
  }

  _initializeState(): void {
    // Get box product data from dataset
    this.variantId = this.dataset.variantId || '';
    this.productHandle = this.dataset.handle;
    this.productTitle = this.dataset.title;
    this.productPrice = parseFloat(this.dataset.price || '0') || 0;
    this.maxQuantity = parseInt(this.dataset.maxQuantity || '999', 10) || 999;
    this.productImage = this.dataset.image || "";
    
    // Initialize or get quantity from store
    this._initializeInStore();
    this.quantity = this._getQuantityFromStore();
    
    // Update UI based on initial state
    this._updateUI();
  }

  _initializeInStore(): void {
    // Initialize box product in store if not already there
    if (!window.mixMatchStore) {
      console.warn("MixMatchStore not initialized");
      return;
    }

    const boxKey = `box_${this.variantId}`;
    const existingBoxProduct = window.mixMatchStore.state.boxProducts[boxKey];
    
    // Only initialize if it doesn't exist yet
    // If it exists, keep the existing quantity (from persisted state)
    if (!existingBoxProduct) {
      window.mixMatchStore.setBoxProductData({
        id: this.variantId,
        quantity: 0,
        title: this.productTitle || '',
        image: this.productImage,
        price: this.productPrice,
        handle: this.productHandle || ''
      });
    }
  }

  _getQuantityFromStore(): number {
    if (!window.mixMatchStore) return 0;
    const boxKey = `box_${this.variantId}`;
    const boxProduct = window.mixMatchStore.state.boxProducts[boxKey];
    return boxProduct ? boxProduct.quantity : 0;
  }

  _updateQuantityInStore(quantity: number): void {
    if (!window.mixMatchStore) {
      console.warn("MixMatchStore not initialized");
      return;
    }

    window.mixMatchStore.setBoxProductData({
      id: this.variantId,
      quantity: quantity,
      title: this.productTitle || '',
      image: this.productImage,
      price: this.productPrice,
      handle: this.productHandle || ''
    });
  }

  _bindEvents(): void {
    // Add button click
    if (this.addButtonEl) {
      this.addButtonEl.addEventListener("click", () => {
        this._updateQuantityInStore(1);
      });
    }

    // Plus button click
    if (this.plusButtonEl) {
      this.plusButtonEl.addEventListener("click", () => {
        this._handleQuantityChange(1);
      });
    }

    // Minus button click
    if (this.minusButtonEl) {
      this.minusButtonEl.addEventListener("click", () => {
        this._handleQuantityChange(-1);
      });
    }
  }

  _handleQuantityChange(change: number): void {
    const newQuantity = this.quantity + change;
    
    // Validate quantity bounds
    if (newQuantity < 0 || newQuantity > this.maxQuantity) {
      return;
    }

    this._updateQuantityInStore(newQuantity);
  }

  _updateUI(): void {
    // Toggle between add button and quantity picker
    if (this.quantity === 0) {
      this.addButtonEl?.classList.remove("tw-hidden");
      this.quantityPickerEl?.classList.add("tw-hidden");
    } else {
      this.addButtonEl?.classList.add("tw-hidden");
      this.quantityPickerEl?.classList.remove("tw-hidden");
    }

    // Update quantity input value
    if (this.quantityInputEl) {
      this.quantityInputEl.value = String(this.quantity);
      this.quantityInputEl.setAttribute("value", String(this.quantity));
    }

    // Update plus button state
    if (this.plusButtonEl) {
      if (this.quantity >= this.maxQuantity) {
        this.plusButtonEl.setAttribute("disabled", "true");
      } else {
        this.plusButtonEl.removeAttribute("disabled");
      }
    }

    // Update minus button state
    if (this.minusButtonEl) {
      if (this.quantity <= 0) {
        this.minusButtonEl.setAttribute("disabled", "true");
      } else {
        this.minusButtonEl.removeAttribute("disabled");
      }
    }
  }

  onStateChange({ action, previousState, state }: { action: string; previousState: any; state: any }): void {
    // React to box product quantity changes
    if (action === "UPDATE_BOX_PRODUCT_QUANTITY") {
      this.quantity = this._getQuantityFromStore();
      this._updateUI();
    }
  }
}

